import { useState } from 'react'
import {
  X, ChevronRight, ChevronLeft, Check, Info,
  AlertTriangle, Plus, Trash2
} from 'lucide-react'
import { Badge } from '../../components/ui'

/**
 * PepWizard — Wizard creazione PEP a 2 step
 *
 * Step 1: Tipo progetto + contesto iniziale
 * Step 2: Primo obiettivo + indicatore
 *
 * Props:
 *   minoreId     - ID del minore
 *   minoreNome   - Nome del minore (per il titolo)
 *   onConfirm    - callback(pepData) — chiama POST /api/pep
 *   onClose      - callback per chiudere il wizard
 */

// ── Costanti ────────────────────────────────────────────────

const ENTE_OPTIONS = [
  { value: 'servizi_sociali', label: 'Servizi sociali' },
  { value: 'tribunale_minori', label: 'Tribunale per i minorenni' },
  { value: 'scuola', label: 'Scuola' },
  { value: 'famiglia', label: 'Famiglia' },
  { value: 'asl', label: 'ASL' },
  { value: 'comunita', label: 'Comunità' },
  { value: 'altro', label: 'Altro' }
]

const FASE_OPTIONS = [
  { value: 'accoglienza', label: 'Accoglienza' },
  { value: 'osservazione', label: 'Osservazione' },
  { value: 'progetto_attivo', label: 'Progetto attivo' },
  { value: 'verifica', label: 'Verifica' },
  { value: 'chiusura', label: 'Chiusura' }
]

const AREA_OPTIONS = [
  { value: 'autonomia', label: 'Autonomia' },
  { value: 'socializzazione', label: 'Socializzazione' },
  { value: 'gestione_emotiva', label: 'Gestione emotiva' },
  { value: 'apprendimento', label: 'Apprendimento' },
  { value: 'relazioni_familiari', label: 'Relazioni familiari' },
  { value: 'comportamento', label: 'Comportamento' },
  { value: 'cura_di_se', label: 'Cura di sé' },
  { value: 'rispetto_regole', label: 'Rispetto regole' },
  { value: 'motivazione', label: 'Motivazione' },
  { value: 'altro', label: 'Altro' }
]

const FREQUENZA_OPTIONS = [
  { value: 'mensile', label: 'Mensile' },
  { value: 'bimestrale', label: 'Bimestrale' },
  { value: 'trimestrale', label: 'Trimestrale' },
  { value: 'semestrale', label: 'Semestrale' },
  { value: 'personalizzata', label: 'Personalizzata' }
]

const REGOLE_COLLEGABILI = [
  { value: 'R01', label: 'R01 — Trend presenze', unit: '% presenze' },
  { value: 'R08', label: 'R08 — Trend comportamenti problematici', unit: 'episodi/settimana' },
  { value: 'R11', label: 'R11 — Settimane senza episodi', unit: 'settimane consecutive' },
  { value: 'R12', label: 'R12 — Trend svolgimento compiti', unit: '% compiti autonomi' },
  { value: 'R14', label: 'R14 — Partecipazione attività di gruppo', unit: '% giorni con attività' },
  { value: 'R15', label: 'R15 — Varietà attività', unit: 'attività diverse' },
  { value: 'R17', label: 'R17 — Frequenza segnalazioni urgenti', unit: 'segnalazioni/periodo' }
]

const ESITO_OPTIONS = [
  { value: 'raggiunto', label: 'Raggiunto' },
  { value: 'parziale', label: 'Parziale' },
  { value: 'non_raggiunto', label: 'Non raggiunto' },
  { value: 'ricalibrato', label: 'Ricalibrato' },
  { value: 'sospeso', label: 'Sospeso' }
]

// ── Componente principale ───────────────────────────────────

export default function PepWizard({ minoreId, minoreNome, onConfirm, onClose }) {
  const [step, setStep] = useState(1)
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  // Step 1 state
  const [tipo, setTipo] = useState('nuovo')
  const [dataAvvio, setDataAvvio] = useState(toDateInputValue(new Date()))
  const [faseCorrente, setFaseCorrente] = useState('accoglienza')
  const [enteTipo, setEnteTipo] = useState('')
  const [enteNome, setEnteNome] = useState('')
  const [referente, setReferente] = useState('')
  const [motivoInserimento, setMotivoInserimento] = useState('')

  // Step 2 state
  const [obArea, setObArea] = useState('')
  const [obAreaCustom, setObAreaCustom] = useState('')
  const [obTitolo, setObTitolo] = useState('')
  const [obPriorita, setObPriorita] = useState('media')
  const [obIndicatoreTipo, setObIndicatoreTipo] = useState('qualitativo')
  const [obRegolaId, setObRegolaId] = useState('')
  const [obBaseline, setObBaseline] = useState('')
  const [obTarget, setObTarget] = useState('')
  const [obEtichettaValore, setObEtichettaValore] = useState('')
  const [obCriterio, setObCriterio] = useState('')
  const [obFrequenza, setObFrequenza] = useState('trimestrale')
  const [obStrategia, setObStrategia] = useState('')

  // Verifiche storiche (solo se progetto già in corso)
  const [verificheStoriche, setVerificheStoriche] = useState([])
  const [showVerificheStoriche, setShowVerificheStoriche] = useState(false)

  const isStorico = tipo === 'gia_in_corso'

  // ── Validazione ─────────────────────────────────────────

  const validateStep1 = () => {
    const errs = {}
    if (!dataAvvio) errs.dataAvvio = 'La data di avvio è obbligatoria'
    if (!motivoInserimento.trim()) errs.motivoInserimento = 'Il motivo di inserimento è obbligatorio'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const validateStep2 = () => {
    const errs = {}
    if (!obArea) errs.obArea = "L'area è obbligatoria"
    if (obArea === 'altro' && !obAreaCustom.trim()) errs.obAreaCustom = 'Specifica l\'area'
    if (!obTitolo.trim()) errs.obTitolo = 'Il titolo è obbligatorio'
    if (obIndicatoreTipo === 'automatico') {
      if (!obRegolaId) errs.obRegolaId = 'Seleziona una regola'
      if (obBaseline === '') errs.obBaseline = 'Il valore baseline è obbligatorio'
      if (obTarget === '') errs.obTarget = 'Il valore target è obbligatorio'
    }
    if (obIndicatoreTipo === 'qualitativo' && !obCriterio.trim()) {
      errs.obCriterio = 'Il criterio qualitativo è obbligatorio'
    }
    // Valida verifiche storiche
    verificheStoriche.forEach((v, i) => {
      if (!v.dataEvento) errs[`verifica_${i}_data`] = 'Data obbligatoria'
      if (!v.esito) errs[`verifica_${i}_esito`] = 'Esito obbligatorio'
    })
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  // ── Navigazione step ──────────────────────────────────

  const goNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2)
    }
  }

  const goBack = () => {
    if (step === 2) setStep(1)
  }

  // ── Submit ────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!validateStep2()) return

    setSubmitting(true)
    try {
      // Costruisci obiettivo
      const obiettivo = {
        area: obArea,
        ...(obArea === 'altro' && { areaCustom: obAreaCustom.trim() }),
        titolo: obTitolo.trim(),
        priorita: obPriorita,
        frequenzaVerifica: obFrequenza,
        indicatore: {
          tipo: obIndicatoreTipo
        }
      }

      if (obIndicatoreTipo === 'automatico') {
        obiettivo.indicatore.regolaInsightId = obRegolaId
        obiettivo.indicatore.valoreBaseline = Number(obBaseline)
        obiettivo.indicatore.valoreTarget = Number(obTarget)
        obiettivo.indicatore.etichettaValore = obEtichettaValore.trim() ||
          REGOLE_COLLEGABILI.find(r => r.value === obRegolaId)?.unit || ''
      } else {
        obiettivo.indicatore.criterio = obCriterio.trim()
      }

      if (obStrategia.trim()) {
        obiettivo.strategia = obStrategia.trim()
      }

      // Aggiungi verifiche storiche
      if (isStorico && verificheStoriche.length > 0) {
        obiettivo.verifiche = verificheStoriche
          .filter(v => v.dataEvento && v.esito)
          .map(v => ({
            dataEvento: v.dataEvento,
            esito: v.esito,
            note: v.note || '',
            valoreQuantitativo: v.valoreQuantitativo !== '' ? Number(v.valoreQuantitativo) : null,
            isDatoStorico: true
          }))
      }

      // Costruisci payload PEP
      const pepData = {
        minoreId,
        dataAvvio: new Date(dataAvvio).toISOString(),
        faseCorrente,
        contestoIniziale: {
          enteInviante: {
            ...(enteTipo && { tipo: enteTipo }),
            ...(enteNome.trim() && { nome: enteNome.trim() }),
            ...(referente.trim() && { referente: referente.trim() })
          },
          motivoInserimento: motivoInserimento.trim()
        },
        obiettivi: [obiettivo]
      }

      await onConfirm(pepData)
    } catch {
      // errore gestito dal parent
    } finally {
      setSubmitting(false)
    }
  }

  // ── Auto-fill unità quando cambia regola ──────────────

  const handleRegolaChange = (value) => {
    setObRegolaId(value)
    const regola = REGOLE_COLLEGABILI.find(r => r.value === value)
    if (regola) {
      setObEtichettaValore(regola.unit)
    }
  }

  // ── Verifiche storiche helpers ────────────────────────

  const addVerificaStorica = () => {
    setVerificheStoriche(prev => [...prev, {
      dataEvento: '',
      esito: '',
      note: '',
      valoreQuantitativo: ''
    }])
  }

  const updateVerificaStorica = (idx, field, value) => {
    setVerificheStoriche(prev => prev.map((v, i) =>
      i === idx ? { ...v, [field]: value } : v
    ))
  }

  const removeVerificaStorica = (idx) => {
    setVerificheStoriche(prev => prev.filter((_, i) => i !== idx))
  }

  // ── Render ────────────────────────────────────────────

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Avvia Progetto Educativo
            </h2>
            {minoreNome && (
              <p className="text-sm text-gray-500 mt-0.5">{minoreNome}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Stepper */}
        <div className="px-6 py-3 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-center gap-4">
            <StepDot number={1} active={step === 1} completed={step > 1} label="Progetto" />
            <div className={`w-16 h-0.5 ${step > 1 ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
            <StepDot number={2} active={step === 2} completed={false} label="Obiettivo" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {step === 1 ? (
            <Step1
              tipo={tipo} setTipo={setTipo}
              dataAvvio={dataAvvio} setDataAvvio={setDataAvvio}
              faseCorrente={faseCorrente} setFaseCorrente={setFaseCorrente}
              enteTipo={enteTipo} setEnteTipo={setEnteTipo}
              enteNome={enteNome} setEnteNome={setEnteNome}
              referente={referente} setReferente={setReferente}
              motivoInserimento={motivoInserimento} setMotivoInserimento={setMotivoInserimento}
              errors={errors}
            />
          ) : (
            <Step2
              obArea={obArea} setObArea={setObArea}
              obAreaCustom={obAreaCustom} setObAreaCustom={setObAreaCustom}
              obTitolo={obTitolo} setObTitolo={setObTitolo}
              obPriorita={obPriorita} setObPriorita={setObPriorita}
              obIndicatoreTipo={obIndicatoreTipo} setObIndicatoreTipo={setObIndicatoreTipo}
              obRegolaId={obRegolaId} handleRegolaChange={handleRegolaChange}
              obBaseline={obBaseline} setObBaseline={setObBaseline}
              obTarget={obTarget} setObTarget={setObTarget}
              obEtichettaValore={obEtichettaValore} setObEtichettaValore={setObEtichettaValore}
              obCriterio={obCriterio} setObCriterio={setObCriterio}
              obFrequenza={obFrequenza} setObFrequenza={setObFrequenza}
              obStrategia={obStrategia} setObStrategia={setObStrategia}
              isStorico={isStorico}
              showVerificheStoriche={showVerificheStoriche}
              setShowVerificheStoriche={setShowVerificheStoriche}
              verificheStoriche={verificheStoriche}
              addVerificaStorica={addVerificaStorica}
              updateVerificaStorica={updateVerificaStorica}
              removeVerificaStorica={removeVerificaStorica}
              errors={errors}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          {step === 1 ? (
            <>
              <button
                onClick={onClose}
                className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                Annulla
              </button>
              <button
                onClick={goNext}
                className="btn-primary flex items-center"
              >
                Avanti
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={goBack}
                className="flex items-center text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Indietro
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="btn-primary flex items-center"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Creazione...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-1" />
                    Crea progetto
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Stepper dot ─────────────────────────────────────────────

function StepDot({ number, active, completed, label }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
        completed
          ? 'bg-primary-500 text-white'
          : active
            ? 'bg-primary-500 text-white ring-4 ring-primary-100 dark:ring-primary-900/30'
            : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
      }`}>
        {completed ? <Check className="h-4 w-4" /> : number}
      </div>
      <span className={`text-xs ${active || completed ? 'text-primary-600 font-medium' : 'text-gray-400'}`}>
        {label}
      </span>
    </div>
  )
}

// ── Step 1: Progetto ────────────────────────────────────────

function Step1({
  tipo, setTipo,
  dataAvvio, setDataAvvio,
  faseCorrente, setFaseCorrente,
  enteTipo, setEnteTipo,
  enteNome, setEnteNome,
  referente, setReferente,
  motivoInserimento, setMotivoInserimento,
  errors
}) {
  return (
    <div className="space-y-5">
      {/* Tipo progetto */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Tipo di progetto
        </label>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setTipo('nuovo')}
            className={`flex-1 p-3 rounded-lg border-2 text-left transition-colors ${
              tipo === 'nuovo'
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
            }`}
          >
            <p className={`font-medium text-sm ${tipo === 'nuovo' ? 'text-primary-700 dark:text-primary-400' : 'text-gray-700 dark:text-gray-300'}`}>
              Nuovo progetto
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Il minore inizia oggi il percorso
            </p>
          </button>
          <button
            type="button"
            onClick={() => setTipo('gia_in_corso')}
            className={`flex-1 p-3 rounded-lg border-2 text-left transition-colors ${
              tipo === 'gia_in_corso'
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
            }`}
          >
            <p className={`font-medium text-sm ${tipo === 'gia_in_corso' ? 'text-primary-700 dark:text-primary-400' : 'text-gray-700 dark:text-gray-300'}`}>
              Già in corso
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Progetto avviato prima della piattaforma
            </p>
          </button>
        </div>
      </div>

      {/* Data avvio + Fase corrente */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Data avvio *
          </label>
          <input
            type="date"
            value={dataAvvio}
            onChange={(e) => setDataAvvio(e.target.value)}
            max={tipo === 'nuovo' ? toDateInputValue(new Date()) : undefined}
            className={`form-input w-full ${errors.dataAvvio ? 'border-red-500' : ''}`}
          />
          {errors.dataAvvio && <p className="text-xs text-red-500 mt-1">{errors.dataAvvio}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Fase corrente
          </label>
          <select
            value={faseCorrente}
            onChange={(e) => setFaseCorrente(e.target.value)}
            className="form-input w-full"
          >
            {FASE_OPTIONS.map(f => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Ente inviante */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Ente inviante
        </label>
        <div className="grid grid-cols-2 gap-4">
          <select
            value={enteTipo}
            onChange={(e) => setEnteTipo(e.target.value)}
            className="form-input"
          >
            <option value="">Seleziona tipo...</option>
            {ENTE_OPTIONS.map(e => (
              <option key={e.value} value={e.value}>{e.label}</option>
            ))}
          </select>
          <input
            type="text"
            value={enteNome}
            onChange={(e) => setEnteNome(e.target.value)}
            placeholder="Nome ente (es. Comune di...)"
            className="form-input"
            maxLength={200}
          />
        </div>
      </div>

      {/* Referente */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Referente ente
        </label>
        <input
          type="text"
          value={referente}
          onChange={(e) => setReferente(e.target.value)}
          placeholder="Nome e cognome del referente"
          className="form-input w-full"
          maxLength={100}
        />
      </div>

      {/* Motivo inserimento */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Motivo inserimento *
        </label>
        <textarea
          value={motivoInserimento}
          onChange={(e) => setMotivoInserimento(e.target.value)}
          placeholder="Descrivi brevemente il motivo dell'inserimento nel centro..."
          rows={4}
          className={`form-input w-full resize-none ${errors.motivoInserimento ? 'border-red-500' : ''}`}
          maxLength={3000}
        />
        {errors.motivoInserimento && <p className="text-xs text-red-500 mt-1">{errors.motivoInserimento}</p>}
        <p className="text-xs text-gray-400 text-right mt-1">{motivoInserimento.length}/3000</p>
      </div>

      {/* Callout */}
      <div className="flex items-start gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
        <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-blue-700 dark:text-blue-400">
          Situazione familiare, bisogni e punti di forza potrai aggiungerli dopo dalla scheda del progetto.
        </p>
      </div>
    </div>
  )
}

// ── Step 2: Obiettivo ───────────────────────────────────────

function Step2({
  obArea, setObArea,
  obAreaCustom, setObAreaCustom,
  obTitolo, setObTitolo,
  obPriorita, setObPriorita,
  obIndicatoreTipo, setObIndicatoreTipo,
  obRegolaId, handleRegolaChange,
  obBaseline, setObBaseline,
  obTarget, setObTarget,
  obEtichettaValore, setObEtichettaValore,
  obCriterio, setObCriterio,
  obFrequenza, setObFrequenza,
  obStrategia, setObStrategia,
  isStorico,
  showVerificheStoriche, setShowVerificheStoriche,
  verificheStoriche,
  addVerificaStorica, updateVerificaStorica, removeVerificaStorica,
  errors
}) {
  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-500">
        Definisci il primo obiettivo del progetto. Potrai aggiungerne altri in seguito.
      </p>

      {/* Area + Titolo */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Area di intervento *
          </label>
          <select
            value={obArea}
            onChange={(e) => setObArea(e.target.value)}
            className={`form-input w-full ${errors.obArea ? 'border-red-500' : ''}`}
          >
            <option value="">Seleziona...</option>
            {AREA_OPTIONS.map(a => (
              <option key={a.value} value={a.value}>{a.label}</option>
            ))}
          </select>
          {errors.obArea && <p className="text-xs text-red-500 mt-1">{errors.obArea}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Priorità
          </label>
          <div className="flex gap-2 mt-1.5">
            {['alta', 'media', 'bassa'].map(p => (
              <button
                key={p}
                type="button"
                onClick={() => setObPriorita(p)}
                className={`flex-1 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                  obPriorita === p
                    ? p === 'alta' ? 'border-red-400 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                    : p === 'media' ? 'border-orange-400 bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400'
                    : 'border-green-400 bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                    : 'border-gray-200 dark:border-gray-600 text-gray-500 hover:border-gray-300'
                }`}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Area custom se "altro" */}
      {obArea === 'altro' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Specifica area *
          </label>
          <input
            type="text"
            value={obAreaCustom}
            onChange={(e) => setObAreaCustom(e.target.value)}
            placeholder="Descrivi l'area..."
            className={`form-input w-full ${errors.obAreaCustom ? 'border-red-500' : ''}`}
            maxLength={100}
          />
          {errors.obAreaCustom && <p className="text-xs text-red-500 mt-1">{errors.obAreaCustom}</p>}
        </div>
      )}

      {/* Titolo */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Titolo obiettivo *
        </label>
        <input
          type="text"
          value={obTitolo}
          onChange={(e) => setObTitolo(e.target.value)}
          placeholder="Es: Ridurre episodi aggressivi, Migliorare autonomia nei compiti..."
          className={`form-input w-full ${errors.obTitolo ? 'border-red-500' : ''}`}
          maxLength={200}
        />
        {errors.obTitolo && <p className="text-xs text-red-500 mt-1">{errors.obTitolo}</p>}
      </div>

      {/* Toggle indicatore */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Tipo indicatore di verifica
        </label>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setObIndicatoreTipo('qualitativo')}
            className={`flex-1 p-3 rounded-lg border-2 text-left transition-colors ${
              obIndicatoreTipo === 'qualitativo'
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
            }`}
          >
            <p className={`font-medium text-sm ${obIndicatoreTipo === 'qualitativo' ? 'text-primary-700 dark:text-primary-400' : 'text-gray-700 dark:text-gray-300'}`}>
              Qualitativo
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Criterio descrittivo definito dal coordinatore
            </p>
          </button>
          <button
            type="button"
            onClick={() => setObIndicatoreTipo('automatico')}
            className={`flex-1 p-3 rounded-lg border-2 text-left transition-colors ${
              obIndicatoreTipo === 'automatico'
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
            }`}
          >
            <p className={`font-medium text-sm ${obIndicatoreTipo === 'automatico' ? 'text-primary-700 dark:text-primary-400' : 'text-gray-700 dark:text-gray-300'}`}>
              Automatico
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Collegato ai dati del diario (con baseline e target)
            </p>
          </button>
        </div>
      </div>

      {/* Indicatore qualitativo */}
      {obIndicatoreTipo === 'qualitativo' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Criterio di verifica *
          </label>
          <textarea
            value={obCriterio}
            onChange={(e) => setObCriterio(e.target.value)}
            placeholder="Es: Il minore riesce a gestire la frustrazione senza reagire fisicamente"
            rows={3}
            className={`form-input w-full resize-none ${errors.obCriterio ? 'border-red-500' : ''}`}
            maxLength={500}
          />
          {errors.obCriterio && <p className="text-xs text-red-500 mt-1">{errors.obCriterio}</p>}
        </div>
      )}

      {/* Indicatore automatico */}
      {obIndicatoreTipo === 'automatico' && (
        <div className="space-y-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Regola insight *
            </label>
            <select
              value={obRegolaId}
              onChange={(e) => handleRegolaChange(e.target.value)}
              className={`form-input w-full ${errors.obRegolaId ? 'border-red-500' : ''}`}
            >
              <option value="">Seleziona regola...</option>
              {REGOLE_COLLEGABILI.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            {errors.obRegolaId && <p className="text-xs text-red-500 mt-1">{errors.obRegolaId}</p>}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Baseline *
              </label>
              <input
                type="number"
                value={obBaseline}
                onChange={(e) => setObBaseline(e.target.value)}
                placeholder="0"
                className={`form-input w-full ${errors.obBaseline ? 'border-red-500' : ''}`}
              />
              {errors.obBaseline && <p className="text-xs text-red-500 mt-1">{errors.obBaseline}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Target *
              </label>
              <input
                type="number"
                value={obTarget}
                onChange={(e) => setObTarget(e.target.value)}
                placeholder="0"
                className={`form-input w-full ${errors.obTarget ? 'border-red-500' : ''}`}
              />
              {errors.obTarget && <p className="text-xs text-red-500 mt-1">{errors.obTarget}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Unità
              </label>
              <input
                type="text"
                value={obEtichettaValore}
                onChange={(e) => setObEtichettaValore(e.target.value)}
                placeholder="es: episodi/mese"
                className="form-input w-full"
                maxLength={50}
              />
            </div>
          </div>

          {obBaseline !== '' && obTarget !== '' && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>Obiettivo: da <strong>{obBaseline}</strong> a <strong>{obTarget}</strong> {obEtichettaValore}</span>
            </div>
          )}
        </div>
      )}

      {/* Frequenza verifica */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Frequenza verifica
        </label>
        <select
          value={obFrequenza}
          onChange={(e) => setObFrequenza(e.target.value)}
          className="form-input w-full"
        >
          {FREQUENZA_OPTIONS.map(f => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
      </div>

      {/* Strategia */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Strategia / intervento previsto
          <span className="text-gray-400 font-normal ml-1">(opzionale)</span>
        </label>
        <textarea
          value={obStrategia}
          onChange={(e) => setObStrategia(e.target.value)}
          placeholder="Come si intende raggiungere l'obiettivo..."
          rows={3}
          className="form-input w-full resize-none"
          maxLength={2000}
        />
      </div>

      {/* Verifiche storiche (solo se progetto già in corso) */}
      {isStorico && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <button
            type="button"
            onClick={() => setShowVerificheStoriche(!showVerificheStoriche)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600"
          >
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Aggiungi verifiche storiche passate
            <Badge variant="default" className="text-[10px]">opzionale</Badge>
          </button>

          {showVerificheStoriche && (
            <div className="mt-3 space-y-3">
              <p className="text-xs text-gray-500">
                Inserisci le verifiche effettuate prima dell'adozione della piattaforma.
              </p>

              {verificheStoriche.map((v, idx) => (
                <div key={idx} className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 mb-0.5">Data *</label>
                      <input
                        type="date"
                        value={v.dataEvento}
                        onChange={(e) => updateVerificaStorica(idx, 'dataEvento', e.target.value)}
                        className={`form-input w-full text-sm ${errors[`verifica_${idx}_data`] ? 'border-red-500' : ''}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-0.5">Esito *</label>
                      <select
                        value={v.esito}
                        onChange={(e) => updateVerificaStorica(idx, 'esito', e.target.value)}
                        className={`form-input w-full text-sm ${errors[`verifica_${idx}_esito`] ? 'border-red-500' : ''}`}
                      >
                        <option value="">Seleziona...</option>
                        {ESITO_OPTIONS.map(es => (
                          <option key={es.value} value={es.value}>{es.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-0.5">Valore</label>
                      <input
                        type="number"
                        value={v.valoreQuantitativo}
                        onChange={(e) => updateVerificaStorica(idx, 'valoreQuantitativo', e.target.value)}
                        placeholder="Opzionale"
                        className="form-input w-full text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-0.5">Note</label>
                      <input
                        type="text"
                        value={v.note}
                        onChange={(e) => updateVerificaStorica(idx, 'note', e.target.value)}
                        placeholder="Opzionale"
                        className="form-input w-full text-sm"
                        maxLength={2000}
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => removeVerificaStorica(idx)}
                    className="text-red-400 hover:text-red-600 mt-4"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={addVerificaStorica}
                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
              >
                <Plus className="h-3.5 w-3.5" />
                Aggiungi verifica
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Helpers ─────────────────────────────────────────────────

function toDateInputValue(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}
